/**
 * @fileoverview Memory Game Play Screen
 * @description Active memory game with card grid, timer, and stats
 */

import React, { useState, useCallback, useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { router, useLocalSearchParams } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/lib/supabase/client";
import {
  useMemoryGame,
  MemoryBoard,
  GameStatsBar,
  MemoryResultOverlay,
  GridSize,
  ImageSetType,
  MemoryGameResult,
} from "@/modules/memory";
import { notifyTeamOfPoints } from "@/modules/games/utils/teamNotifications";
import { logActivity, ActivityActionType } from "@/lib/activity-log";

/**
 * MemoryGameScreen - Active game
 * Uses activeProfile to support guardian mode - when a guardian switches to
 * a dependent, games are played as that dependent.
 */
export default function MemoryGameScreen() {
  const colors = useThemeColors();
  const { sessionId } = useLocalSearchParams<{ sessionId: string }>();
  const { user, activeProfile, userTeam } = useAuth();
  
  // Use activeProfile for game actions (supports guardian mode)
  // When playing as a dependent, this is the dependent's ID
  const targetUserId = activeProfile?.id;
  const targetProfile = activeProfile;

  const [showResult, setShowResult] = useState(false);
  const [gameResult, setGameResult] = useState<MemoryGameResult | null>(null);
  const [sessionMode, setSessionMode] = useState<"free" | "weekend">("free");
  const [weekendId, setWeekendId] = useState<string | null>(null);
  const [gridSize, setGridSize] = useState<GridSize>("4x4");
  const [imageSetType, setImageSetType] = useState<ImageSetType>("random");
  const [familyImages, setFamilyImages] = useState<string[]>([]);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [weekendPointsEarned, setWeekendPointsEarned] = useState<number>(0);

  // Versus race state
  const [sessionType, setSessionType] = useState<"solo" | "versus">("solo");
  const [isWaitingForOpponent, setIsWaitingForOpponent] = useState(false);
  const [opponentInfo, setOpponentInfo] = useState<{ id: string; name: string } | null>(null);
  const [myReady, setMyReady] = useState(false);
  const [opponentReady, setOpponentReady] = useState(false);
  const [opponentTime, setOpponentTime] = useState<number | null>(null);
  const [isRaceWinner, setIsRaceWinner] = useState<boolean | null>(null);
  const [raceComplete, setRaceComplete] = useState(false);
  const [waitingForOpponentFinish, setWaitingForOpponentFinish] = useState(false);
  const [myFinishTime, setMyFinishTime] = useState<number | null>(null);

  /**
   * Load session config
   */
  useEffect(() => {
    const loadSession = async () => {
      // Wait for both sessionId AND activeProfile to be available
      if (!sessionId || !targetUserId) return;

      try {
        const { data: session } = await supabase
          .from("game_sessions")
          .select("mode, weekend_id, game_state, session_type, status, participants")
          .eq("id", sessionId)
          .single();

        if (session) {
          setSessionMode(session.mode as "free" | "weekend");
          setWeekendId(session.weekend_id);
          setSessionType(session.session_type as "solo" | "versus");

          const gameState = session.game_state as Record<string, unknown> | null;

          if (gameState?.grid_size) {
            setGridSize(gameState.grid_size as GridSize);
          }
          if (gameState?.image_set_type) {
            setImageSetType(gameState.image_set_type as ImageSetType);
          }

          // Handle versus mode
          if (session.session_type === "versus") {
            const participants = session.participants as Array<{ user_id: string; name: string }>;
            // Use targetUserId for opponent detection (supports guardian mode)
            const opponent = participants?.find((p) => p.user_id !== targetUserId);
            if (opponent) {
              setOpponentInfo({ id: opponent.user_id, name: opponent.name });
            }

            // Check waiting/ready status
            if (session.status === "waiting") {
              setIsWaitingForOpponent(true);
              const playerReady = gameState?.player_ready as Record<string, boolean> | undefined;
              if (playerReady && targetUserId) {
                setMyReady(playerReady[targetUserId] === true);
                if (opponent) {
                  setOpponentReady(playerReady[opponent.user_id] === true);
                }
              }
            } else {
              setIsWaitingForOpponent(false);
            }

            // Check opponent's time if they finished
            const playerTimes = gameState?.player_times as Record<string, number> | undefined;
            if (playerTimes && opponent && targetUserId) {
              const myTime = playerTimes[targetUserId];
              const oppTime = playerTimes[opponent.user_id];
              
              if (oppTime !== undefined) {
                setOpponentTime(oppTime);
              }
              
              // Check if race is already complete (both times exist)
              if (myTime !== undefined && oppTime !== undefined && session.status === "completed") {
                const iWon = myTime < oppTime;
                setIsRaceWinner(iWon);
                setRaceComplete(true);
                setWaitingForOpponentFinish(false);
                setMyFinishTime(myTime);
                setShowResult(true);
                
                // Set game result for display
                setGameResult({
                  completed: true,
                  moves: 0,
                  timeSeconds: myTime,
                  pairs: 0,
                  isPerfect: false,
                  score: 0,
                });
              } else if (myTime !== undefined && oppTime === undefined) {
                // I finished but opponent hasn't - show waiting modal
                setWaitingForOpponentFinish(true);
                setMyFinishTime(myTime);
                setShowResult(true);
                setGameResult({
                  completed: true,
                  moves: 0,
                  timeSeconds: myTime,
                  pairs: 0,
                  isPerfect: false,
                  score: 0,
                });
              }
            }
          }
        }

        // Load family images if needed
        const gameState = session?.game_state as Record<string, unknown> | null;
        if (gameState?.image_set_type === "family") {
          const { data: imageSet } = await supabase
            .from("memory_image_sets")
            .select("images")
            .eq("type", "family")
            .eq("is_active", true)
            .single();

          if (imageSet?.images) {
            setFamilyImages(imageSet.images as string[]);
          }
        }

        setConfigLoaded(true);
      } catch (err) {
        console.error("Error loading session:", err);
        setConfigLoaded(true);
      }
    };

    loadSession();
  }, [sessionId, targetUserId]);

  /**
   * Helper to update user stats (called from both handleGameEnd and realtime)
   * Stores difficulty-specific stats in the stats JSONB field
   */
  const updateUserStats = useCallback(
    async (timeSeconds: number, isWin: boolean, isPerfect: boolean, difficulty: GridSize = gridSize) => {
      if (!targetUserId) return;

      try {
        // Get stats for this mode (weekend or free play) for active profile
        const { data: existingStats } = await supabase
          .from("user_game_stats")
          .select("*")
          .eq("user_id", targetUserId)
          .eq("game_slug", "puzzle_matching")
          .eq("mode", sessionMode)
          .single();

        const currentBestTime = existingStats?.personal_best_time_ms ?? null;
        const newBestTimeMs = timeSeconds * 1000;
        const newBestTime =
          currentBestTime === null
            ? newBestTimeMs
            : Math.min(currentBestTime, newBestTimeMs);

        // Build difficulty-specific stats
        const currentStats = (existingStats?.stats as {
          perfect_games?: number;
          difficulty_stats?: Record<string, { games_played: number; best_time_ms: number | null; wins: number }>;
        }) ?? {};

        const difficultyStats = currentStats.difficulty_stats ?? {};
        const currentDiffStats = difficultyStats[difficulty] ?? { games_played: 0, best_time_ms: null, wins: 0 };
        
        difficultyStats[difficulty] = {
          games_played: currentDiffStats.games_played + 1,
          best_time_ms: currentDiffStats.best_time_ms === null 
            ? newBestTimeMs 
            : Math.min(currentDiffStats.best_time_ms, newBestTimeMs),
          wins: currentDiffStats.wins + (isWin ? 1 : 0),
        };

        if (existingStats) {
          await supabase
            .from("user_game_stats")
            .update({
              games_played: existingStats.games_played + 1,
              games_won: existingStats.games_won + (isWin ? 1 : 0),
              games_lost: existingStats.games_lost + (!isWin ? 1 : 0),
              personal_best_time_ms: newBestTime,
              stats: {
                ...currentStats,
                perfect_games: (currentStats.perfect_games ?? 0) + (isPerfect ? 1 : 0),
                difficulty_stats: difficultyStats,
                last_difficulty: difficulty,
              },
              last_played_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            })
            .eq("id", existingStats.id);
        } else {
          await supabase.from("user_game_stats").insert({
            user_id: targetUserId,
            game_slug: "puzzle_matching",
            games_played: 1,
            games_won: isWin ? 1 : 0,
            games_lost: !isWin ? 1 : 0,
            personal_best_time_ms: newBestTimeMs,
            stats: {
              perfect_games: isPerfect ? 1 : 0,
              difficulty_stats: difficultyStats,
              last_difficulty: difficulty,
            },
            mode: sessionMode,
          });
        }
      } catch (err) {
        console.error("Error updating user stats:", err);
      }
    },
    [targetUserId, sessionMode, gridSize]
  );

  /**
   * Subscribe to realtime updates for versus mode
   */
  useEffect(() => {
    if (!sessionId || sessionType !== "versus") return;

    const channel = supabase
      .channel(`memory_versus:${sessionId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "game_sessions",
          filter: `id=eq.${sessionId}`,
        },
        (payload) => {
          const newSession = payload.new as {
            status: string;
            game_state: Record<string, unknown>;
          };

          // Check ready status (use targetUserId for guardian mode)
          const playerReady = newSession.game_state?.player_ready as Record<string, boolean> | undefined;
          if (playerReady && targetUserId && opponentInfo) {
            setMyReady(playerReady[targetUserId] === true);
            setOpponentReady(playerReady[opponentInfo.id] === true);

            // Both ready? Game can start
            if (playerReady[targetUserId] && playerReady[opponentInfo.id]) {
              setIsWaitingForOpponent(false);
            }
          }

          // Check opponent's completion time
          const playerTimes = newSession.game_state?.player_times as Record<string, number> | undefined;
          if (playerTimes && opponentInfo && targetUserId) {
            const myTime = playerTimes[targetUserId];
            const oppTime = playerTimes[opponentInfo.id];
            
            if (oppTime !== undefined) {
              setOpponentTime(oppTime);
            }
            
            // Both finished? Determine winner and show result
            // This triggers for the FIRST player when the second player finishes
            if (myTime !== undefined && oppTime !== undefined) {
              // Use functional updates to avoid stale closure issues
              setRaceComplete((prevRaceComplete) => {
                if (!prevRaceComplete) {
                  const iWon = myTime < oppTime;
                  setIsRaceWinner(iWon);
                  setWaitingForOpponentFinish(false);
                  setShowResult(true);
                  
                  // Set game result for the overlay
                  setGameResult({
                    completed: true,
                    moves: 0, // We don't have exact moves here, but time is what matters
                    timeSeconds: myTime,
                    pairs: 0,
                    isPerfect: false,
                    score: 0,
                  });
                  
                  // Update my stats now that race is complete
                  updateUserStats(myTime, iWon, false, gridSize);
                  
                  return true; // Set raceComplete to true
                }
                return prevRaceComplete;
              });
            }
          }

          // Update waiting status based on session status
          if (newSession.status === "active") {
            setIsWaitingForOpponent(false);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [sessionId, sessionType, targetUserId, opponentInfo, updateUserStats]);

  /**
   * Refresh game state manually (for waiting modal)
   */
  const handleRefreshGameState = useCallback(async () => {
    if (!sessionId || !targetUserId) return;
    
    try {
      const { data: session } = await supabase
        .from("game_sessions")
        .select("game_state, status, winner_user_id, participants")
        .eq("id", sessionId)
        .single();
      
      if (!session) return;
      
      const gameState = session.game_state as Record<string, unknown>;
      const playerTimes = gameState?.player_times as Record<string, number> | undefined;
      
      if (!playerTimes) return;
      
      // Get opponent from session (use targetUserId for guardian mode)
      const participants = session.participants as Array<{ user_id: string; name: string }>;
      const opponent = participants?.find((p) => p.user_id !== targetUserId);
      
      if (!opponent || !targetUserId) return;
      
      const myTime = playerTimes[targetUserId];
      const oppTime = playerTimes[opponent.user_id];
      
      // If opponent time now exists, race is complete
      if (myTime !== undefined && oppTime !== undefined) {
        const iWon = myTime < oppTime;
        setIsRaceWinner(iWon);
        setOpponentTime(oppTime);
        setRaceComplete(true);
        setWaitingForOpponentFinish(false);
        setShowResult(true);
        
        // Update my stats if not already done
        await updateUserStats(myTime, iWon, false);
        
        console.log("Refresh found race complete:", { myTime, oppTime, iWon });
      }
    } catch (err) {
      console.error("Error refreshing game state:", err);
    }
  }, [sessionId, targetUserId, updateUserStats]);

  /**
   * Handle ready button press (uses targetUserId for guardian mode)
   */
  const handleReady = async () => {
    if (!targetUserId || !sessionId) return;

    setMyReady(true);

    try {
      // Get current game state
      const { data: session } = await supabase
        .from("game_sessions")
        .select("game_state")
        .eq("id", sessionId)
        .single();

      const gameState = session?.game_state as Record<string, unknown> || {};
      const playerReady = (gameState.player_ready as Record<string, boolean>) || {};

      playerReady[targetUserId] = true;

      // Check if both players are ready
      const bothReady = opponentInfo && playerReady[opponentInfo.id] === true;

      await supabase
        .from("game_sessions")
        .update({
          game_state: { ...gameState, player_ready: playerReady },
          ...(bothReady && { status: "active" }),
        })
        .eq("id", sessionId);

      if (bothReady) {
        setIsWaitingForOpponent(false);
      }
    } catch (err) {
      console.error("Error setting ready:", err);
    }
  };

  /**
   * Helper to award weekend points and notify admins
   */
  const awardWeekendPoints = useCallback(
    async (winnerId: string, teamId: string, timeSeconds: number, isPerfect: boolean, loserId?: string) => {
      if (!weekendId) return;

      try {
        const { data: gameConfig } = await supabase
          .from("weekend_game_config")
          .select("points_config")
          .eq("weekend_id", weekendId)
          .eq("game_slug", "puzzle_matching")
          .single();

        const config = gameConfig?.points_config as {
          win?: number;
          completion?: number;
          speed_bonus?: number;
          perfect_memory_bonus?: number;
        } | null;

        let points = sessionType === "versus" 
          ? (config?.win ?? 30) 
          : (config?.completion ?? 20);
        
        if (isPerfect) {
          points += config?.perfect_memory_bonus ?? 10;
        }
        
        // Add speed bonus for solo mode
        if (sessionType === "solo") {
          const speedBonus = Math.max(0, Math.floor((120 - timeSeconds) / 10));
          points += speedBonus;
        }

        // Track points earned for UI display (only if winner is active profile)
        if (winnerId === targetUserId) {
          setWeekendPointsEarned(points);
        }

        // Get winner's profile for notification
        const { data: winnerProfile } = await supabase
          .from("profiles")
          .select("full_name")
          .eq("id", winnerId)
          .single();

        // Only the WINNER's device should insert into score_history and update team score
        // (guardian mode: check if active profile is the winner)
        if (winnerId === targetUserId) {
          // Get current team total
          const { data: teamData } = await supabase
            .from("teams")
            .select("total_score, name")
            .eq("id", teamId)
            .single();

          const previousTotal = teamData?.total_score ?? 0;
          const newTotal = previousTotal + points;

          // Insert into score_history with game_id (session_id) for tracking
          const reason = sessionType === "versus"
            ? `Memory tijd-race gewonnen!`
            : isPerfect
            ? "Memory perfect afgerond!"
            : "Memory afgerond";

          await supabase.from("score_history").insert({
            team_id: teamId,
            points: points,
            previous_total: previousTotal,
            new_total: newTotal,
            reason: reason,
            game_id: sessionId,
            game_type: "memory",
            awarded_to: winnerId,
            awarded_by: winnerId,
          });

          // Update team total score
          await supabase
            .from("teams")
            .update({ total_score: newTotal })
            .eq("id", teamId);

          // Update game session with points awarded
          await supabase
            .from("game_sessions")
            .update({ points_awarded: points })
            .eq("id", sessionId);

          // Notify team
          await notifyTeamOfPoints({
            earnerId: winnerId,
            earnerName: winnerProfile?.full_name ?? "Iemand",
            teamId: teamId,
            points,
            gameName: "Memory",
            reason: sessionType === "versus" ? "win" : (isPerfect ? "perfect" : "completion"),
            weekendId: weekendId ?? undefined,
            sessionId: sessionId ?? undefined,
            isWin: sessionType === "versus",
          });

          // Log activity for the game completion
          await logActivity({
            user_id: winnerId,
            action_type: "game.end" as ActivityActionType,
            action_category: "game",
            target_type: "game",
            target_id: sessionId ?? undefined,
            target_name: "Memory",
            metadata: {
              session_type: sessionType,
              mode: sessionMode,
              points_earned: points,
              time_seconds: timeSeconds,
              difficulty: gridSize,
              is_perfect: isPerfect,
              team_name: teamData?.name ?? undefined,
            },
            weekend_id: weekendId ?? undefined,
          });
        }

        // Note: We don't send in-app notifications to familie admins for game completions
        // as this would be too noisy. Activity logs are sufficient for tracking.
      } catch (err) {
        console.error("Error awarding weekend points:", err);
      }
    },
    [weekendId, sessionType, sessionMode, targetUserId, sessionId, gridSize]
  );

  /**
   * Format time for logging (MM:SS)
   */
  const formatTimeForLog = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  /**
   * Handle game end
   */
  const handleGameEnd = useCallback(
    async (result: MemoryGameResult) => {
      setGameResult(result);
      setMyFinishTime(result.timeSeconds);

      if (!targetUserId) return;

      try {
        // Solo mode: show result immediately and update stats
        if (sessionType === "solo") {
          setShowResult(true);
          await updateUserStats(result.timeSeconds, true, result.isPerfect, gridSize);
          
          // Award weekend points for solo (use targetUserId for guardian mode)
          if (sessionMode === "weekend" && weekendId && userTeam?.id) {
            await awardWeekendPoints(targetUserId, userTeam.id, result.timeSeconds, result.isPerfect);
          }

          // Log activity for solo game (only for weekend mode to keep logs focused)
          if (sessionMode === "weekend" && targetUserId) {
            await logActivity({
              user_id: targetUserId,
              action_type: "game.end" as ActivityActionType,
              action_category: "game",
              target_type: "game",
              target_id: sessionId ?? undefined,
              target_name: "Memory",
              metadata: {
                session_type: "solo",
                mode: sessionMode,
                time_seconds: result.timeSeconds,
                moves: result.moves,
                difficulty: gridSize,
                is_perfect: result.isPerfect,
              },
              weekend_id: weekendId ?? undefined,
            });
          }
          return;
        }

        // Versus mode: check if opponent has finished
        if (sessionType === "versus") {
          const { data: session } = await supabase
            .from("game_sessions")
            .select("game_state, participants")
            .eq("id", sessionId)
            .single();

          if (!session) {
            console.error("Session not found");
            return;
          }

          const gameState = (session.game_state as Record<string, unknown>) || {};
          const playerTimes = (gameState.player_times as Record<string, number>) || {};
          
          // Get opponent info from session if not set in state (use targetUserId)
          let opponent = opponentInfo;
          if (!opponent) {
            const participants = session.participants as Array<{ user_id: string; name: string }>;
            const oppData = participants?.find((p) => p.user_id !== targetUserId);
            if (oppData) {
              opponent = { id: oppData.user_id, name: oppData.name };
              setOpponentInfo(opponent);
            }
          }
          
          if (!opponent) {
            console.error("Could not find opponent info");
            return;
          }
          
          // Save my time (use targetUserId for guardian mode)
          playerTimes[targetUserId] = result.timeSeconds;
          
          // Check if opponent has finished
          const opponentFinishTime = playerTimes[opponent.id];
          const bothFinished = opponentFinishTime !== undefined;
          
          if (bothFinished) {
            // I'm the SECOND player to finish - determine winner and complete race
            // Use targetUserId for guardian mode
            const iWon = result.timeSeconds < opponentFinishTime;
            const winnerId = iWon ? targetUserId : opponent.id;
            const loserId = iWon ? opponent.id : targetUserId;
            
            setIsRaceWinner(iWon);
            setOpponentTime(opponentFinishTime);
            setRaceComplete(true);
            setWaitingForOpponentFinish(false);
            setShowResult(true);

            // Update session as completed
            await supabase
              .from("game_sessions")
              .update({
                game_state: { ...gameState, player_times: playerTimes },
                status: "completed",
                completed_at: new Date().toISOString(),
                winner_user_id: winnerId,
              })
              .eq("id", sessionId);

            // Update stats for myself
            await updateUserStats(result.timeSeconds, iWon, result.isPerfect, gridSize);

            // Award weekend points to the WINNER
            if (sessionMode === "weekend" && weekendId) {
              const winnerTeamMember = await supabase
                .from("team_members")
                .select("team_id, teams!inner(weekend_id)")
                .eq("user_id", winnerId)
                .eq("teams.weekend_id", weekendId)
                .single();
              
              if (winnerTeamMember.data?.team_id) {
                const winnerTime = iWon ? result.timeSeconds : opponentFinishTime;
                await awardWeekendPoints(winnerId, winnerTeamMember.data.team_id, winnerTime, false, loserId);
              }
            }

            // Send notification to opponent (first player) that race is complete
            const myTimeFormatted = `${Math.floor(result.timeSeconds / 60)}:${(result.timeSeconds % 60).toString().padStart(2, "0")}`;
            const oppTimeFormatted = `${Math.floor(opponentFinishTime / 60)}:${(opponentFinishTime % 60).toString().padStart(2, "0")}`;
            
            await supabase.from("notifications").insert({
              user_id: opponent.id,
              type: "game_result",
              title: iWon ? "Memory Race Verloren" : "Memory Race Gewonnen! 🏆",
              body: iWon 
                ? `${targetProfile?.full_name ?? "Tegenstander"} was sneller! (${myTimeFormatted} vs ${oppTimeFormatted})`
                : `Je hebt gewonnen met ${oppTimeFormatted} vs ${myTimeFormatted}!`,
              action_url: `/(tabs)/games/memory/${sessionId}`,
              sender_id: targetUserId,
              weekend_id: weekendId,
              notification_type: iWon ? "game_lost" : "game_won",
              lottie_animation: iWon ? "Holding_Back_Tears" : "confetti",
              priority: "high",
            });
          } else {
            // I'm the FIRST player to finish - show waiting modal
            setWaitingForOpponentFinish(true);
            setShowResult(true); // Show the waiting modal
            
            // Save my time to the database
            await supabase
              .from("game_sessions")
              .update({
                game_state: { ...gameState, player_times: playerTimes },
              })
              .eq("id", sessionId);
            
            // Don't update stats yet - wait for opponent to finish
          }
        }
      } catch (err) {
        console.error("Error in handleGameEnd:", err);
      }
    },
    [targetUserId, targetProfile, weekendId, sessionMode, userTeam, sessionId, sessionType, opponentInfo, updateUserStats, awardWeekendPoints, gridSize]
  );

  // Initialize game hook
  const {
    cards,
    revealedCards,
    gameStatus,
    moves,
    matchedPairs,
    totalPairs,
    elapsedTime,
    isLoading,
    error,
    disabled,
    gridConfig,
    flipCard,
    startGame,
    resetGame,
    pauseGame,
    resumeGame,
  } = useMemoryGame({
    sessionId: sessionId ?? "",
    gridSize,
    imageSetType,
    familyImages,
    onGameEnd: handleGameEnd,
  });

  /**
   * Start game when config is loaded (solo mode or when both players ready in versus)
   */
  useEffect(() => {
    if (configLoaded && gameStatus === "idle" && cards.length > 0) {
      // For solo, start immediately
      // For versus, only start when not waiting for opponent
      if (sessionType === "solo" || !isWaitingForOpponent) {
        startGame();
      }
    }
  }, [configLoaded, gameStatus, cards.length, startGame, sessionType, isWaitingForOpponent]);

  /**
   * Handle play again
   */
  const handlePlayAgain = useCallback(async () => {
    // Reset UI state
    setShowResult(false);
    setGameResult(null);
    setIsRaceWinner(null);
    setRaceComplete(false);
    setWaitingForOpponentFinish(false);
    setOpponentTime(null);
    setMyFinishTime(null);
    setWeekendPointsEarned(0);

    if (sessionType === "versus") {
      // For versus mode, go back to start a new challenge
      router.replace("/(tabs)/games/memory");
    } else {
      // For solo mode, reset and start a new game
      await resetGame();
      setTimeout(() => startGame(), 100);
    }
  }, [resetGame, startGame, sessionType]);

  /**
   * Handle exit - checks if can go back, otherwise navigates to games
   */
  const handleExit = useCallback(() => {
    if (router.canGoBack()) {
      router.back();
    } else {
      router.replace("/(tabs)/games");
    }
  }, []);

  /**
   * Handle pause
   */
  const handlePause = useCallback(() => {
    if (gameStatus === "playing") {
      pauseGame();
      Alert.alert(
        "Gepauzeerd",
        "Het spel is gepauzeerd. Druk op doorgaan om verder te spelen.",
        [{ text: "Doorgaan", onPress: resumeGame }]
      );
    }
  }, [gameStatus, pauseGame, resumeGame]);

  if (!configLoaded || isLoading) {
    return (
      <SafeAreaView
        style={[styles.container, { backgroundColor: colors.background }]}
        edges={["top", "left", "right"]}
      >
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#8B5CF6" />
          <Text style={[styles.loadingText, { color: colors.textSecondary }]}>
            Spel laden...
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  if (error) {
    return (
      <SafeAreaView
        style={[styles.container, { backgroundColor: colors.background }]}
        edges={["top", "left", "right"]}
      >
        <View style={styles.errorContainer}>
          <Ionicons name="alert-circle" size={64} color="#EF4444" />
          <Text style={[styles.errorText, { color: colors.text }]}>
            {error}
          </Text>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => {
              if (router.canGoBack()) {
                router.back();
              } else {
                router.replace("/(tabs)/games");
              }
            }}
          >
            <Text style={styles.backButtonText}>Terug</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  // Waiting for opponent screen (versus mode)
  if (sessionType === "versus" && isWaitingForOpponent) {
    return (
      <SafeAreaView
        style={[styles.container, { backgroundColor: colors.background }]}
        edges={["top", "left", "right"]}
      >
        <View style={styles.header}>
          <TouchableOpacity
            onPress={handleExit}
            style={styles.headerButton}
            accessibilityLabel="Terug"
            accessibilityRole="button"
          >
            <Ionicons name="arrow-back" size={24} color={colors.text} />
          </TouchableOpacity>
          <View style={styles.headerCenter}>
            <Text style={[styles.headerTitle, { color: colors.text }]}>
              Memory Tijd-Race
            </Text>
            <Text style={[styles.headerSubtitle, { color: colors.textSecondary }]}>
              vs {opponentInfo?.name ?? "Tegenstander"}
            </Text>
          </View>
          <View style={styles.headerButton} />
        </View>

        <View style={styles.waitingContainer}>
          <View style={[styles.waitingCard, { backgroundColor: colors.surface }]}>
            {/* <Text style={styles.waitingEmoji}>⏱️</Text> */}
            <Text style={[styles.waitingTitle, { color: colors.text }]}>
              Tijd-Race
            </Text>
            <Text style={[styles.waitingSubtitle, { color: colors.textSecondary }]}>
              Race tegen {opponentInfo?.name} - wie vindt alle paren het snelst?
            </Text>

            <View style={styles.readyContainer}>
              {/* My status */}
              <View style={[styles.readyCard, myReady && styles.readyCardActive]}>
                <Ionicons
                  name={myReady ? "checkmark-circle" : "ellipse-outline"}
                  size={32}
                  color={myReady ? "#22C55E" : colors.textTertiary}
                />
                <Text style={[styles.readyName, { color: colors.text }]}>Jij</Text>
                <Text style={[styles.readyStatus, { color: myReady ? "#22C55E" : colors.textSecondary }]}>
                  {myReady ? "Klaar!" : "Niet klaar"}
                </Text>
              </View>

              <Text style={styles.vsText}>VS</Text>

              {/* Opponent status */}
              <View style={[styles.readyCard, opponentReady && styles.readyCardActive]}>
                <Ionicons
                  name={opponentReady ? "checkmark-circle" : "ellipse-outline"}
                  size={32}
                  color={opponentReady ? "#22C55E" : colors.textTertiary}
                />
                <Text style={[styles.readyName, { color: colors.text }]}>
                  {opponentInfo?.name ?? "..."}
                </Text>
                <Text style={[styles.readyStatus, { color: opponentReady ? "#22C55E" : colors.textSecondary }]}>
                  {opponentReady ? "Klaar!" : "Wachten..."}
                </Text>
              </View>
            </View>

            {!myReady ? (
              <TouchableOpacity
                style={styles.readyButton}
                onPress={handleReady}
                accessibilityLabel="Ik ben klaar"
                accessibilityRole="button"
              >
                <Ionicons name="checkmark" size={24} color="#FFFFFF" />
                <Text style={styles.readyButtonText}>Ik ben klaar!</Text>
              </TouchableOpacity>
            ) : (
              <View style={styles.waitingMessageContainer}>
                <ActivityIndicator color="#F59E0B" />
                <Text style={[styles.waitingMessage, { color: colors.textSecondary }]}>
                  Wachten op {opponentInfo?.name}...
                </Text>
              </View>
            )}
          </View>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: colors.background }]}
      edges={["top", "left", "right"]}
    >
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          onPress={handleExit}
          style={styles.headerButton}
          accessibilityLabel="Terug"
          accessibilityRole="button"
        >
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </TouchableOpacity>

        <View style={styles.headerCenter}>
          <Text style={[styles.headerTitle, { color: colors.text }]}>
            Memory
          </Text>
          <Text style={[styles.headerSubtitle, { color: colors.textSecondary }]}>
            {gridConfig.label}
          </Text>
        </View>

        {gameStatus === "playing" && (
          <TouchableOpacity
            onPress={handlePause}
            style={styles.headerButton}
            accessibilityLabel="Pauzeren"
            accessibilityRole="button"
          >
            <Ionicons name="pause-circle" size={28} color="#F59E0B" />
          </TouchableOpacity>
        )}
      </View>

      {/* Stats Bar */}
      <GameStatsBar
        moves={moves}
        matchedPairs={matchedPairs}
        totalPairs={totalPairs}
        elapsedTime={elapsedTime}
        isPaused={gameStatus === "paused"}
      />

      {/* Game Board */}
      <View style={styles.boardContainer}>
        {gameStatus === "idle" ? (
          <TouchableOpacity
            style={styles.startOverlay}
            onPress={startGame}
            accessibilityLabel="Start spel"
            accessibilityRole="button"
          >
            <Ionicons name="play-circle" size={80} color="#8B5CF6" />
            <Text style={styles.startText}>Tik om te starten</Text>
          </TouchableOpacity>
        ) : (
          <MemoryBoard
            cards={cards}
            revealedCards={revealedCards}
            disabled={disabled}
            gridSize={gridSize}
            onCardPress={flipCard}
          />
        )}
      </View>

      {/* Hint text */}
      {gameStatus === "playing" && (
        <View style={styles.hintContainer}>
          <Text style={[styles.hintText, { color: colors.textSecondary }]}>
            Vind alle paren door kaarten om te draaien
          </Text>
        </View>
      )}

      {/* Result Overlay */}
      <MemoryResultOverlay
        visible={showResult}
        result={gameResult}
        mode={sessionMode}
        weekendPoints={weekendPointsEarned}
        isVersusMode={sessionType === "versus"}
        isRaceWinner={isRaceWinner}
        opponentName={opponentInfo?.name}
        opponentTime={opponentTime}
        waitingForOpponent={waitingForOpponentFinish}
        onPlayAgain={handlePlayAgain}
        onExit={handleExit}
        onClose={() => setShowResult(false)}
        onRefresh={handleRefreshGameState}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 8,
    justifyContent: "space-between",
  },
  headerButton: {
    padding: 12,
    width: 56,
    alignItems: "center",
  },
  headerCenter: {
    alignItems: "center",
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: "600",
  },
  headerSubtitle: {
    fontSize: 12,
    marginTop: 2,
  },
  boardContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  startOverlay: {
    alignItems: "center",
    gap: 16,
  },
  startText: {
    fontSize: 18,
    color: "#9CA3AF",
    fontWeight: "500",
  },
  hintContainer: {
    padding: 16,
    alignItems: "center",
  },
  hintText: {
    fontSize: 13,
    textAlign: "center",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    gap: 16,
  },
  loadingText: {
    fontSize: 16,
  },
  errorContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    gap: 16,
    padding: 24,
  },
  errorText: {
    fontSize: 18,
    textAlign: "center",
  },
  backButton: {
    backgroundColor: "#8B5CF6",
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 12,
    marginTop: 8,
  },
  backButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  // Waiting screen styles
  waitingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  waitingCard: {
    width: "100%",
    borderRadius: 24,
    padding: 24,
    alignItems: "center",
    borderWidth: 2,
    borderColor: "#F59E0B",
  },
  waitingEmoji: {
    fontSize: 64,
    marginBottom: 8,
  },
  waitingTitle: {
    fontSize: 24,
    fontWeight: "700",
    marginBottom: 8,
  },
  waitingSubtitle: {
    fontSize: 14,
    textAlign: "center",
    marginBottom: 24,
  },
  readyContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
    marginBottom: 24,
  },
  readyCard: {
    alignItems: "center",
    padding: 16,
    borderRadius: 16,
    backgroundColor: "rgba(0, 0, 0, 0.2)",
    minWidth: 100,
  },
  readyCardActive: {
    backgroundColor: "rgba(34, 197, 94, 0.1)",
    borderWidth: 1,
    borderColor: "#22C55E",
  },
  readyName: {
    fontSize: 14,
    fontWeight: "600",
    marginTop: 8,
  },
  readyStatus: {
    fontSize: 12,
    marginTop: 2,
  },
  vsText: {
    fontSize: 20,
    fontWeight: "700",
    color: "#F59E0B",
  },
  readyButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    backgroundColor: "#22C55E",
    paddingVertical: 14,
    paddingHorizontal: 32,
    borderRadius: 16,
    width: "100%",
  },
  readyButtonText: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "600",
  },
  waitingMessageContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  waitingMessage: {
    fontSize: 14,
  },
});

